import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Vehicle {
  id: string;
  registration: string;
  alias: string;
  vehicle_type: string;
  status: 'driving' | 'idle' | 'stopped' | 'engine_on' | 'unknown';
  last_event: string;
  event_date: string;
  latitude: number;
  longitude: number;
  location: string;
  poi_name: string;
  street: string;
  heading: number;
  speed_kph: number;
  speed_limit_kph: number;
  state_duration_seconds: number | null;
  gps_accurate: boolean;
  driver_name: string;
  driver_phone: string;
  fuel_level: number;
  odometer_km: number;
  battery_level: number;
  engine_hours: number;
  vehicle_class: string;
  icon_colour: string;
  division_id: string;
  label: string;
  notes: string;
}

interface VehicleSummary {
  total_vehicles: number;
  driving: number;
  idle: number;
  stopped: number;
  no_signal: number;
}

interface VehiclesResponse {
  vehicles: Vehicle[];
  summary: VehicleSummary;
  timestamp: string;
}

interface RoutePoint { lat: number; lng: number; speed: number; time: string; }

interface Journey {
  start_time: string; start_address: string; start_postcode: string;
  end_time: string; end_address: string; end_postcode: string;
  max_speed_kph: number; distance_km: number; duration_minutes: number;
  overspeed_count: number; idle_count: number; route_points: RoutePoint[];
  start_lat: number; start_lng: number; end_lat: number; end_lng: number;
}

interface DaySummary {
  journey_count: number; total_distance_km: number; total_duration_minutes: number;
  max_speed_kph: number; overspeed_events: number; event_count: number;
}

interface HistoryResponse {
  vehicle_id: string; registration: string; date: string;
  journeys: Journey[]; day_summary: DaySummary; raw_event_count: number;
}

interface FleetMatchEntry {
  ram_id?: string; registration?: string; ram_registration?: string;
  fleet_id?: string; fleet_registration?: string; fleet_make?: string;
  fleet_model?: string; alias?: string; vehicle_type?: string;
  ram_type?: string; status?: string; driver_name?: string; matched?: boolean;
}

interface FleetMatchResponse {
  matched: FleetMatchEntry[];
  unmatched_ram: FleetMatchEntry[];
  unmatched_fleet: FleetMatchEntry[];
  summary: { ram_vehicles: number; fleet_vehicles: number; matched: number; };
}

interface JobTrackingEntry {
  job_id: number; job_number: string; job_status: string;
  customer_name: string; vehicle_reg: string; driver_name: string;
  vehicle_lat: number; vehicle_lng: number; vehicle_moving: boolean;
  tracking_status: string; tracking_label: string;
  distance_to_collection_km: number | null; distance_to_delivery_km: number | null;
  confidence: string; collection_postcode: string; delivery_postcode: string;
}

interface JobStatusResponse {
  jobs: JobTrackingEntry[];
  summary: { total: number; status_breakdown: Record<string, number> };
  timestamp: string;
}

type TabId = 'live-map' | 'fleet-intelligence' | 'journey-history' | 'job-tracker' | 'fleet-match';

// ---------------------------------------------------------------------------
// Constants & Helpers
// ---------------------------------------------------------------------------

const KM_TO_MILES = 0.621371;
const AUTO_REFRESH_SECONDS = 30;

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}` : '';

async function apiFetch<T>(path: string): Promise<T> {
  const token = localStorage.getItem('tourx_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

function kmToMi(km: number): string { return (km * KM_TO_MILES).toFixed(1); }
function fmtDist(km: number): string { return `${km.toFixed(1)} km (${kmToMi(km)} mi)`; }
function fmtDur(min: number): string {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
}
function relTime(ds: string): string {
  const diff = Math.floor((Date.now() - new Date(ds).getTime()) / 1000);
  if (diff < 0) return 'now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function fmtTime(ds: string): string {
  return new Date(ds).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function todayISO(): string { return new Date().toISOString().slice(0, 10); }
function fmtStateDuration(secs: number | null): string {
  if (secs === null || secs === undefined) return '';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
function stateDurationLabel(v: { status: string; state_duration_seconds: number | null }): string {
  const dur = fmtStateDuration(v.state_duration_seconds);
  if (!dur) return '';
  const labels: Record<string, string> = {
    driving: 'Driving for', idle: 'Idle for', engine_on: 'Engine on for',
    stopped: 'Stopped for', unknown: 'Offline for',
  };
  return `${labels[v.status] || 'In state for'} ${dur}`;
}
function locationDisplay(v: { poi_name: string; location: string; street: string; latitude: number; longitude: number }): string {
  const parts: string[] = [];
  if (v.poi_name) parts.push(v.poi_name);
  if (v.location) parts.push(v.location);
  else if (v.street) parts.push(v.street);
  if (parts.length === 0 && v.latitude && v.longitude) parts.push(`${v.latitude.toFixed(4)}, ${v.longitude.toFixed(4)}`);
  return parts.join(' — ') || 'Unknown';
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string; mapColor: string }> = {
  driving:   { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500', mapColor: '#10b981' },
  idle:      { bg: 'bg-amber-100',   text: 'text-amber-800',   dot: 'bg-amber-500',   mapColor: '#f59e0b' },
  engine_on: { bg: 'bg-amber-100',   text: 'text-amber-800',   dot: 'bg-amber-500',   mapColor: '#f59e0b' },
  stopped:   { bg: 'bg-slate-200',   text: 'text-slate-700',   dot: 'bg-slate-400',   mapColor: '#94a3b8' },
  unknown:   { bg: 'bg-red-100',     text: 'text-red-700',     dot: 'bg-red-400',     mapColor: '#ef4444' },
};

const STATUS_LABEL: Record<string, string> = {
  driving: 'Driving', idle: 'Idle', engine_on: 'Engine On', stopped: 'Stopped', unknown: 'No Signal',
};

const TRACKING_STATUS_COLORS: Record<string, string> = {
  en_route_collection: 'text-blue-700 bg-blue-50',
  approaching_collection: 'text-blue-700 bg-blue-100',
  at_collection: 'text-green-700 bg-green-100',
  in_transit: 'text-purple-700 bg-purple-100',
  approaching_delivery: 'text-orange-700 bg-orange-100',
  at_delivery: 'text-green-800 bg-green-200',
  returning: 'text-gray-700 bg-gray-200',
  stationary: 'text-gray-600 bg-gray-100',
  no_tracker: 'text-red-600 bg-red-100',
  unknown: 'text-gray-500 bg-gray-100',
};

// ---------------------------------------------------------------------------
// Reusable Components
// ---------------------------------------------------------------------------

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="mb-4 flex items-center justify-between rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
      <span>{message}</span>
      <button onClick={onDismiss} className="ml-4 font-medium underline hover:text-red-600">Dismiss</button>
    </div>
  );
}

function MetricCard({ label, value, sub, accent, icon }: {
  label: string; value: string | number; sub?: string; accent?: string; icon?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
          <p className={`mt-0.5 text-xl font-bold ${accent || 'text-gray-800'}`}>{value}</p>
          {sub && <p className="mt-0.5 text-[11px] text-gray-500">{sub}</p>}
        </div>
        {icon && <span className="text-2xl opacity-30">{icon}</span>}
      </div>
    </div>
  );
}

function Badge({ status }: { status: string }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function FuelBar({ level }: { level: number }) {
  const pct = Math.max(0, Math.min(100, level || 0));
  const color = pct > 50 ? 'bg-emerald-500' : pct > 20 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] font-medium text-gray-500">{pct}%</span>
    </div>
  );
}

function RefreshBar({ countdown, onRefresh, loading }: { countdown: number; onRefresh: () => void; loading: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <button
        onClick={onRefresh}
        disabled={loading}
        className="rounded-lg border border-blue-600 bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? 'Refreshing...' : 'Refresh Now'}
      </button>
      <div className="flex items-center gap-1.5">
        <div className="relative h-5 w-5">
          <svg viewBox="0 0 36 36" className="h-5 w-5 -rotate-90">
            <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke="#e5e7eb" strokeWidth="3" />
            <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${(countdown / AUTO_REFRESH_SECONDS) * 100}, 100`}
              className="transition-all duration-1000" />
          </svg>
        </div>
        <span className="text-xs text-gray-500">{countdown}s</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: LIVE MAP — Interactive Leaflet map with vehicle markers
// ---------------------------------------------------------------------------

function LiveMapTab({ vehicles, summary, timestamp, loading, onRefresh, countdown }: {
  vehicles: Vehicle[]; summary: VehicleSummary | null; timestamp: string;
  loading: boolean; onRefresh: () => void; countdown: number;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = statusFilter === 'all' ? vehicles : vehicles.filter(v => v.status === statusFilter);

  // Initialise map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [53.5, -1.5],  // UK centre
      zoom: 7,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    markersRef.current = L.layerGroup().addTo(map);
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  // Update markers
  useEffect(() => {
    const markers = markersRef.current;
    const map = mapInstanceRef.current;
    if (!markers || !map) return;
    markers.clearLayers();

    const validVehicles = filtered.filter(v => v.latitude && v.longitude);
    if (validVehicles.length === 0) return;

    validVehicles.forEach(v => {
      const sc = STATUS_COLORS[v.status] || STATUS_COLORS.unknown;
      const heading = v.heading || 0;

      // Create a coloured circle marker with direction indicator
      const icon = L.divIcon({
        className: '',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        html: `<div style="
          width:28px;height:28px;border-radius:50%;
          background:${sc.mapColor};border:3px solid white;
          box-shadow:0 2px 6px rgba(0,0,0,0.3);
          display:flex;align-items:center;justify-content:center;
          color:white;font-size:10px;font-weight:bold;
          position:relative;
        ">
          ${v.status === 'driving' ? `<span style="transform:rotate(${heading}deg);font-size:14px;">&#9650;</span>` : ''}
          ${v.status === 'driving' ? '' : '<span style="font-size:9px;">' + (v.registration || '').slice(-3) + '</span>'}
        </div>`,
      });

      const marker = L.marker([v.latitude, v.longitude], { icon }).addTo(markers);

      const locStr = locationDisplay(v);
      const durStr = stateDurationLabel(v);
      marker.bindPopup(`
        <div style="min-width:240px;font-family:system-ui,sans-serif;">
          <div style="font-weight:700;font-size:14px;margin-bottom:4px;color:#1e293b;">${v.registration}</div>
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${sc.mapColor}"></span>
            <span style="font-size:12px;color:#475569;">${STATUS_LABEL[v.status] || v.status}</span>
            ${durStr ? `<span style="font-size:11px;color:#6b7280;margin-left:auto;font-weight:600;">${durStr}</span>` : ''}
          </div>
          ${v.poi_name ? `<div style="font-size:12px;font-weight:600;color:#1e40af;margin-bottom:3px;">🏢 ${v.poi_name}</div>` : ''}
          <div style="font-size:11px;color:#64748b;margin-bottom:3px;">📍 ${locStr}</div>
          ${v.driver_name ? `<div style="font-size:11px;color:#64748b;margin-bottom:3px;">👤 ${v.driver_name}</div>` : ''}
          <div style="font-size:11px;color:#64748b;margin-bottom:3px;">⛽ ${v.fuel_level || 0}% &nbsp; 🔧 ${v.odometer_km?.toLocaleString() || 0} km &nbsp; ⏱ ${v.engine_hours?.toFixed(0) || 0}h</div>
          ${v.speed_kph > 0 ? `<div style="font-size:11px;color:#64748b;margin-bottom:3px;">🚀 ${v.speed_kph} kph (${Math.round(v.speed_kph * 0.621371)} mph)</div>` : ''}
          <div style="font-size:10px;color:#94a3b8;margin-top:4px;">${relTime(v.event_date)}</div>
        </div>
      `, { maxWidth: 300 });

      marker.on('click', () => setSelectedVehicle(v));
    });

    // Fit bounds if first load
    if (validVehicles.length > 0) {
      const bounds = L.latLngBounds(validVehicles.map(v => [v.latitude, v.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [filtered]);

  return (
    <div className="flex gap-0 h-[calc(100vh-130px)]">
      {/* Map */}
      <div className="flex-1 relative overflow-hidden">
        <div ref={mapRef} className="h-full w-full" />

        {/* Floating KPI bar */}
        {summary && (
          <div className="absolute top-3 left-3 right-3 z-[1000] flex items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg bg-white/95 backdrop-blur-sm px-3 py-2 shadow-lg border border-gray-200">
              <span className="text-xs font-bold text-gray-800">{summary.total_vehicles} vehicles</span>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1 text-xs"><span className="h-2 w-2 rounded-full bg-emerald-500" />{summary.driving} driving</span>
              <span className="flex items-center gap-1 text-xs"><span className="h-2 w-2 rounded-full bg-amber-500" />{summary.idle} idle</span>
              <span className="flex items-center gap-1 text-xs"><span className="h-2 w-2 rounded-full bg-slate-400" />{summary.stopped} stopped</span>
              {summary.no_signal > 0 && (
                <span className="flex items-center gap-1 text-xs"><span className="h-2 w-2 rounded-full bg-red-400" />{summary.no_signal} offline</span>
              )}
            </div>
            <div className="ml-auto">
              <RefreshBar countdown={countdown} onRefresh={onRefresh} loading={loading} />
            </div>
          </div>
        )}

        {/* Status filter pills — bottom of map */}
        <div className="absolute bottom-3 left-3 z-[1000] flex gap-1.5">
          {['all', 'driving', 'idle', 'stopped'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold shadow-sm transition
                ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-white/90 text-gray-700 hover:bg-white border border-gray-200'}`}
            >
              {s === 'all' ? `All (${vehicles.length})` : `${STATUS_LABEL[s]} (${vehicles.filter(v => v.status === s).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Vehicle sidebar */}
      <div className="w-72 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 z-10">
          <h3 className="text-sm font-bold text-gray-800">Fleet ({filtered.length})</h3>
          <p className="text-[10px] text-gray-400 mt-0.5">{timestamp ? `Updated ${fmtTime(timestamp)}` : ''}</p>
        </div>
        <div className="divide-y divide-gray-50">
          {filtered.map(v => {
            const isSelected = selectedVehicle?.id === v.id;
            return (
              <button key={v.id} onClick={() => {
                setSelectedVehicle(v);
                if (mapInstanceRef.current && v.latitude && v.longitude) {
                  mapInstanceRef.current.flyTo([v.latitude, v.longitude], 15, { duration: 0.8 });
                }
              }}
                className={`w-full text-left px-4 py-2.5 transition hover:bg-blue-50 ${isSelected ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-bold text-gray-800">{v.registration}</span>
                  <Badge status={v.status} />
                </div>
                {v.poi_name && (
                  <p className="text-[11px] font-semibold text-blue-700 truncate">🏢 {v.poi_name}</p>
                )}
                <p className="text-[11px] text-gray-500 truncate">
                  📍 {locationDisplay(v)}
                </p>
                {v.driver_name && <p className="text-[11px] text-gray-400">👤 {v.driver_name}</p>}
                {stateDurationLabel(v) && (
                  <p className="text-[10px] font-semibold text-gray-600 mt-0.5">⏱ {stateDurationLabel(v)}</p>
                )}
                <div className="flex items-center gap-3 mt-1">
                  <FuelBar level={v.fuel_level} />
                  {v.speed_kph > 0 && <span className="text-[10px] font-medium text-emerald-600">{v.speed_kph} kph</span>}
                  <span className="ml-auto text-[10px] text-gray-400">{relTime(v.event_date)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: FLEET INTELLIGENCE — Rich analytics dashboard
// ---------------------------------------------------------------------------

function FleetIntelligenceTab({ vehicles, summary, timestamp, loading, onRefresh, countdown }: {
  vehicles: Vehicle[]; summary: VehicleSummary | null; timestamp: string;
  loading: boolean; onRefresh: () => void; countdown: number;
}) {
  const totalOdometer = vehicles.reduce((s, v) => s + (v.odometer_km || 0), 0);
  const totalEngineHours = vehicles.reduce((s, v) => s + (v.engine_hours || 0), 0);
  const avgFuel = vehicles.length ? Math.round(vehicles.reduce((s, v) => s + (v.fuel_level || 0), 0) / vehicles.length) : 0;
  const lowFuelVehicles = vehicles.filter(v => (v.fuel_level || 0) > 0 && v.fuel_level < 20);
  const drivingVehicles = vehicles.filter(v => v.status === 'driving');
  const avgSpeed = drivingVehicles.length
    ? Math.round(drivingVehicles.reduce((s, v) => s + (v.speed_kph || 0), 0) / drivingVehicles.length) : 0;
  const utilisation = summary ? Math.round(((summary.driving + summary.idle) / Math.max(1, summary.total_vehicles)) * 100) : 0;

  // Estimate fleet CO2 (approx 2.68 kg CO2 per litre diesel, ~3.5 km/litre average)
  const estDailyKm = drivingVehicles.length * 150; // rough estimate
  const estCO2kg = Math.round(estDailyKm / 3.5 * 2.68);

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Fleet Intelligence</h2>
          <p className="text-xs text-gray-500">Real-time fleet analytics and insights</p>
        </div>
        <RefreshBar countdown={countdown} onRefresh={onRefresh} loading={loading} />
      </div>

      {loading && vehicles.length === 0 ? <Spinner /> : (
        <>
          {/* KPI Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            <MetricCard label="Total Fleet" value={summary?.total_vehicles || 0} icon="🚛" />
            <MetricCard label="Driving Now" value={summary?.driving || 0} accent="text-emerald-600" sub={`${utilisation}% utilisation`} icon="🟢" />
            <MetricCard label="Idle" value={summary?.idle || 0} accent="text-amber-600" icon="🟡" />
            <MetricCard label="Avg Speed" value={`${avgSpeed} kph`} sub={`${Math.round(avgSpeed * KM_TO_MILES)} mph`} icon="⚡" />
            <MetricCard label="Avg Fuel Level" value={`${avgFuel}%`} accent={avgFuel < 30 ? 'text-red-600' : 'text-emerald-600'} icon="⛽" />
            <MetricCard label="Est. Daily CO₂" value={`${estCO2kg} kg`} sub="Based on active vehicles" icon="🌱" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
            {/* Fleet Status Donut */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-4">Fleet Status Distribution</h3>
              {summary && <StatusDonut summary={summary} />}
            </div>

            {/* Fuel Alert Panel */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Fuel Alerts</h3>
              <p className="text-[11px] text-gray-400 mb-3">{lowFuelVehicles.length} vehicle(s) below 20%</p>
              {lowFuelVehicles.length === 0 ? (
                <div className="text-center py-6 text-gray-400 text-sm">All vehicles above 20% fuel</div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {lowFuelVehicles.sort((a, b) => a.fuel_level - b.fuel_level).map(v => (
                    <div key={v.id} className="flex items-center justify-between rounded-lg bg-red-50 px-3 py-2">
                      <div>
                        <p className="text-xs font-bold text-gray-800">{v.registration}</p>
                        <p className="text-[10px] text-gray-500">{v.driver_name || 'No driver'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-red-600">{v.fuel_level}%</p>
                        <FuelBar level={v.fuel_level} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fleet Totals */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-4">Fleet Totals</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Combined Odometer</span>
                  <span className="text-sm font-bold text-gray-800">{Math.round(totalOdometer).toLocaleString()} km</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Combined Odometer (miles)</span>
                  <span className="text-sm font-bold text-gray-800">{Math.round(totalOdometer * KM_TO_MILES).toLocaleString()} mi</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Total Engine Hours</span>
                  <span className="text-sm font-bold text-gray-800">{Math.round(totalEngineHours).toLocaleString()} hrs</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Vehicles w/ Driver</span>
                  <span className="text-sm font-bold text-gray-800">{vehicles.filter(v => v.driver_name).length} / {vehicles.length}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs text-gray-500">GPS Accuracy</span>
                  <span className="text-sm font-bold text-gray-800">{vehicles.filter(v => v.gps_accurate).length} / {vehicles.length} accurate</span>
                </div>
              </div>
            </div>
          </div>

          {/* Full Vehicle Table with all data */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800">All Vehicles — Detailed View</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Speed</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Fuel</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Odometer</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Eng Hrs</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wider">Last Event</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {vehicles.map(v => (
                    <tr key={v.id} className="hover:bg-blue-50/50 transition">
                      <td className="px-3 py-2 font-bold text-gray-800">{v.registration}</td>
                      <td className="px-3 py-2">
                        <Badge status={v.status} />
                        {stateDurationLabel(v) && (
                          <span className="ml-1.5 text-[10px] text-gray-500">{fmtStateDuration(v.state_duration_seconds)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[220px]">
                        {v.poi_name && <span className="block text-[10px] font-semibold text-blue-700 truncate">{v.poi_name}</span>}
                        <span className="block text-gray-600 truncate text-[11px]" title={locationDisplay(v)}>{locationDisplay(v)}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{v.driver_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{v.speed_kph > 0 ? `${v.speed_kph} kph` : '—'}</td>
                      <td className="px-3 py-2"><FuelBar level={v.fuel_level} /></td>
                      <td className="px-3 py-2 text-gray-600">{v.odometer_km?.toLocaleString()} km</td>
                      <td className="px-3 py-2 text-gray-600">{v.engine_hours?.toFixed(0)}</td>
                      <td className="px-3 py-2 text-gray-500" title={v.last_event}>{relTime(v.event_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatusDonut({ summary }: { summary: VehicleSummary }) {
  const total = summary.total_vehicles || 1;
  const segments = [
    { label: 'Driving', count: summary.driving, color: '#10b981' },
    { label: 'Idle', count: summary.idle, color: '#f59e0b' },
    { label: 'Stopped', count: summary.stopped, color: '#94a3b8' },
    { label: 'Offline', count: summary.no_signal, color: '#ef4444' },
  ].filter(s => s.count > 0);

  let cumulative = 0;
  const radius = 60;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 160 160" className="w-32 h-32">
        {segments.map((seg, i) => {
          const pct = seg.count / total;
          const dashLen = pct * circumference;
          const offset = cumulative * circumference;
          cumulative += pct;
          return (
            <circle key={i} cx="80" cy="80" r={radius} fill="none"
              stroke={seg.color} strokeWidth="16"
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 80 80)" />
          );
        })}
        <text x="80" y="76" textAnchor="middle" className="text-2xl font-bold" fill="#1e293b">{total}</text>
        <text x="80" y="94" textAnchor="middle" className="text-xs" fill="#94a3b8">vehicles</text>
      </svg>
      <div className="space-y-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: seg.color }} />
            <span className="text-xs text-gray-600">{seg.label}</span>
            <span className="text-xs font-bold text-gray-800 ml-auto">{seg.count}</span>
            <span className="text-[10px] text-gray-400 w-8 text-right">{Math.round(seg.count / total * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: JOURNEY HISTORY
// ---------------------------------------------------------------------------

function JourneyHistoryTab({ vehicles }: { vehicles: Vehicle[] }) {
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [date, setDate] = useState(todayISO());
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const routeLayerRef = useRef<L.LayerGroup | null>(null);

  const loadHistory = useCallback(async () => {
    if (!selectedVehicle) return;
    setLoading(true); setError('');
    try {
      const data = await apiFetch<HistoryResponse>(`/api/tracking/history/${selectedVehicle}?date=${date}`);
      setHistory(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally { setLoading(false); }
  }, [selectedVehicle, date]);

  // Init journey map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { center: [53.5, -1.5], zoom: 7 });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap', maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    routeLayerRef.current = L.layerGroup().addTo(map);
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  // Draw journey routes on map
  useEffect(() => {
    const layer = routeLayerRef.current;
    const map = mapInstanceRef.current;
    if (!layer || !map || !history) return;
    layer.clearLayers();

    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#10b981'];
    const allPoints: [number, number][] = [];

    history.journeys.forEach((j, idx) => {
      const pts: [number, number][] = j.route_points
        .filter(p => p.lat && p.lng)
        .map(p => [p.lat, p.lng]);
      if (pts.length < 2) return;
      allPoints.push(...pts);

      const color = colors[idx % colors.length];
      L.polyline(pts, { color, weight: 4, opacity: 0.8 }).addTo(layer);

      // Start marker
      const startIcon = L.divIcon({
        className: '', iconSize: [20, 20], iconAnchor: [10, 10],
        html: `<div style="width:20px;height:20px;border-radius:50%;background:#10b981;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:10px;font-weight:bold;">${idx + 1}</div>`,
      });
      L.marker(pts[0], { icon: startIcon }).addTo(layer)
        .bindPopup(`<b>Journey ${idx + 1} Start</b><br/>${fmtTime(j.start_time)}<br/>${j.start_address}`);

      // End marker
      const endIcon = L.divIcon({
        className: '', iconSize: [20, 20], iconAnchor: [10, 10],
        html: `<div style="width:20px;height:20px;border-radius:50%;background:#ef4444;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:10px;">■</div>`,
      });
      L.marker(pts[pts.length - 1], { icon: endIcon }).addTo(layer)
        .bindPopup(`<b>Journey ${idx + 1} End</b><br/>${fmtTime(j.end_time)}<br/>${j.end_address}`);
    });

    if (allPoints.length > 0) {
      map.fitBounds(L.latLngBounds(allPoints), { padding: [40, 40] });
    }
  }, [history]);

  const maxDur = history ? Math.max(1, ...history.journeys.map(j => j.duration_minutes)) : 1;

  return (
    <div>
      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-600">Vehicle</label>
          <select value={selectedVehicle} onChange={e => setSelectedVehicle(e.target.value)}
            className="w-56 rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">-- Select vehicle --</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration} {v.alias ? `(${v.alias})` : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-600">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <button onClick={loadHistory} disabled={!selectedVehicle || loading}
          className="rounded-lg bg-blue-600 px-5 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading...' : 'Load History'}
        </button>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Journey Map */}
      <div ref={mapRef} className="h-72 rounded-xl border border-gray-200 shadow-sm mb-5" />

      {loading && <Spinner />}

      {!loading && history && (
        <>
          {/* Day KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            <MetricCard label="Journeys" value={history.day_summary.journey_count} icon="🛣️" />
            <MetricCard label="Total Distance" value={fmtDist(history.day_summary.total_distance_km)} icon="📏" />
            <MetricCard label="Driving Time" value={fmtDur(history.day_summary.total_duration_minutes)} icon="⏱" />
            <MetricCard label="Max Speed" value={`${history.day_summary.max_speed_kph} kph`} sub={`${Math.round(history.day_summary.max_speed_kph * KM_TO_MILES)} mph`} icon="⚡" />
            <MetricCard label="Overspeed Events" value={history.day_summary.overspeed_events}
              accent={history.day_summary.overspeed_events > 0 ? 'text-red-600' : 'text-emerald-600'} icon="⚠️" />
          </div>

          {/* Timeline */}
          {history.journeys.length > 0 && (
            <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Journey Timeline</h3>
              <div className="space-y-1.5">
                {history.journeys.map((j, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-10 text-right text-[11px] font-medium text-gray-500">{fmtTime(j.start_time)}</span>
                    <div className="flex-1 flex items-center gap-1">
                      <div className={`h-5 rounded-md ${j.overspeed_count > 0 ? 'bg-gradient-to-r from-blue-500 to-red-400' : 'bg-blue-500'}`}
                        style={{ width: `${Math.max(4, (j.duration_minutes / maxDur) * 100)}%` }}
                        title={`${fmtDur(j.duration_minutes)} | ${fmtDist(j.distance_km)}`} />
                    </div>
                    <span className="text-[11px] text-gray-500">{fmtTime(j.end_time)}</span>
                    <span className="text-[10px] text-gray-400 w-16">{fmtDist(j.distance_km)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Journey cards */}
          <div className="space-y-3">
            {history.journeys.map((j, i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-2">
                  <span className="flex items-center gap-1.5">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">{i + 1}</span>
                    <span className="font-bold text-sm text-gray-800">{fmtTime(j.start_time)} → {fmtTime(j.end_time)}</span>
                  </span>
                  <span className="text-xs text-gray-500">{fmtDur(j.duration_minutes)}</span>
                  <span className="text-xs text-gray-500">{fmtDist(j.distance_km)}</span>
                  <span className="text-xs text-gray-500">Max {j.max_speed_kph} kph ({Math.round(j.max_speed_kph * KM_TO_MILES)} mph)</span>
                  {j.overspeed_count > 0 && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                      {j.overspeed_count} overspeed
                    </span>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-1 text-xs text-gray-600">
                  <div><span className="font-semibold text-gray-700">From:</span> {j.start_address}{j.start_postcode && ` (${j.start_postcode})`}</div>
                  <div><span className="font-semibold text-gray-700">To:</span> {j.end_address}{j.end_postcode && ` (${j.end_postcode})`}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && !history && !error && (
        <p className="py-12 text-center text-sm text-gray-400">Select a vehicle and date, then click Load History.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: JOB TRACKER — Live job tracking with GPS intelligence
// ---------------------------------------------------------------------------

function JobTrackerTab() {
  const [data, setData] = useState<JobStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setData(await apiFetch<JobStatusResponse>('/api/tracking/job-status'));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load job tracking');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Live Job Tracker</h2>
          <p className="text-xs text-gray-500">Automatic job status from live vehicle GPS positions</p>
        </div>
        <button onClick={load} disabled={loading}
          className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {loading && !data && <Spinner />}

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <MetricCard label="Active Jobs" value={data.summary.total} icon="📋" />
            <MetricCard label="In Transit" value={data.summary.status_breakdown?.in_transit || 0} accent="text-purple-600" icon="🚚" />
            <MetricCard label="At Site" value={(data.summary.status_breakdown?.at_collection || 0) + (data.summary.status_breakdown?.at_delivery || 0)} accent="text-emerald-600" icon="📍" />
            <MetricCard label="Approaching" value={(data.summary.status_breakdown?.approaching_collection || 0) + (data.summary.status_breakdown?.approaching_delivery || 0)} accent="text-blue-600" icon="🔵" />
          </div>

          {data.jobs.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white py-12 text-center text-gray-400">
              No active jobs with assigned vehicles
            </div>
          ) : (
            <div className="space-y-3">
              {data.jobs.map(j => {
                const tColor = TRACKING_STATUS_COLORS[j.tracking_status] || TRACKING_STATUS_COLORS.unknown;
                return (
                  <div key={j.job_id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-800">{j.job_number}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${tColor}`}>
                            {j.tracking_label}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${j.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : j.confidence === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                            {j.confidence} confidence
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{j.customer_name || 'Unknown customer'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-gray-700">{j.vehicle_reg}</p>
                        <p className="text-[10px] text-gray-500">{j.driver_name || 'No driver'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="text-emerald-500">●</span>
                        <span>{j.collection_postcode || '?'}</span>
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="text-red-500">●</span>
                        <span>{j.delivery_postcode || '?'}</span>
                      </div>
                      {j.distance_to_collection_km != null && (
                        <span>{j.distance_to_collection_km.toFixed(1)} km to collection</span>
                      )}
                      {j.distance_to_delivery_km != null && (
                        <span>{j.distance_to_delivery_km.toFixed(1)} km to delivery</span>
                      )}
                      <span className={`ml-auto flex items-center gap-1 ${j.vehicle_moving ? 'text-emerald-600' : 'text-gray-400'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${j.vehicle_moving ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
                        {j.vehicle_moving ? 'Moving' : 'Stationary'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: FLEET MATCH
// ---------------------------------------------------------------------------

function FleetMatchTab() {
  const [data, setData] = useState<FleetMatchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch<FleetMatchResponse>('/api/tracking/fleet-match')); }
    catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to load fleet match'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Fleet Match</h2>
          <p className="text-xs text-gray-500">Cross-reference RAM Tracking vehicles with TourX fleet database</p>
        </div>
        <button onClick={load} disabled={loading}
          className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {loading && !data && <Spinner />}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <MetricCard label="RAM Vehicles" value={data.summary.ram_vehicles} icon="📡" />
            <MetricCard label="Fleet Database" value={data.summary.fleet_vehicles} icon="🗄️" />
            <MetricCard label="Matched" value={data.summary.matched} accent="text-emerald-600"
              sub={`${data.summary.fleet_vehicles ? Math.round(data.summary.matched / data.summary.fleet_vehicles * 100) : 0}% coverage`} icon="✅" />
          </div>

          {/* Match progress bar */}
          <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-700">Fleet Coverage</span>
              <span className="text-xs text-gray-500">{data.summary.matched} / {data.summary.ram_vehicles} tracked vehicles matched</span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${data.summary.ram_vehicles ? (data.summary.matched / data.summary.ram_vehicles * 100) : 0}%` }} />
            </div>
          </div>

          {/* Matched */}
          <SectionTable title="Matched Vehicles" count={data.matched.length} color="bg-emerald-100 text-emerald-800"
            headers={['Registration', 'Make/Model', 'Driver', 'Status']}
            rows={data.matched.map(r => [
              r.registration || r.ram_registration || '—',
              [r.fleet_make, r.fleet_model].filter(Boolean).join(' ') || r.ram_type || '—',
              r.driver_name || '—',
              r.status || '—',
            ])} />

          {/* Unmatched RAM */}
          <SectionTable title="Unmatched (Tracked Only)" count={data.unmatched_ram.length} color="bg-amber-100 text-amber-800"
            headers={['Registration', 'Type', 'Driver', 'Status']}
            rows={data.unmatched_ram.map(r => [
              r.registration || r.ram_registration || '—',
              r.ram_type || r.vehicle_type || '—',
              r.driver_name || '—',
              r.status || '—',
            ])} />

          {/* Unmatched Fleet */}
          <SectionTable title="Unmatched (Fleet DB Only)" count={data.unmatched_fleet.length} color="bg-red-100 text-red-700"
            headers={['Registration', 'Make', 'Model']}
            rows={data.unmatched_fleet.map(r => [
              r.registration || r.fleet_registration || '—',
              r.fleet_make || '—',
              r.fleet_model || '—',
            ])} />
        </>
      )}
    </div>
  );
}

function SectionTable({ title, count, color, headers, rows }: {
  title: string; count: number; color: string; headers: string[]; rows: string[][];
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${color}`}>{count}</span>
      </div>
      {rows.length === 0 ? (
        <p className="py-4 text-sm text-gray-400 bg-white rounded-lg border border-gray-200 text-center">None</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {headers.map(h => <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-gray-500">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {row.map((cell, j) => <td key={j} className="px-3 py-2 text-gray-700">{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'live-map', label: 'Live Map', icon: '🗺️' },
  { id: 'fleet-intelligence', label: 'Fleet Intelligence', icon: '📊' },
  { id: 'journey-history', label: 'Journey History', icon: '🛣️' },
  { id: 'job-tracker', label: 'Job Tracker', icon: '📍' },
  { id: 'fleet-match', label: 'Fleet Match', icon: '🔗' },
];

export default function TrackingPage() {
  const [activeTab, setActiveTab] = useState<TabId>('live-map');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [summary, setSummary] = useState<VehicleSummary | null>(null);
  const [timestamp, setTimestamp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS);

  const fetchVehicles = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await apiFetch<VehiclesResponse>('/api/tracking/vehicles');
      setVehicles(data.vehicles);
      setSummary(data.summary);
      setTimestamp(data.timestamp);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load vehicle data');
    } finally {
      setLoading(false);
      setCountdown(AUTO_REFRESH_SECONDS);
    }
  }, []);

  // Initial load
  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // Auto-refresh countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchVehicles();
          return AUTO_REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchVehicles]);

  return (
    <PortalLayout currentPage="tracking">
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="border-b border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Vehicle Tracking</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Live fleet positions, journey analytics, and intelligent job tracking
              </p>
            </div>
            <div className="flex items-center gap-3">
              {timestamp && (
                <span className="text-[11px] text-gray-400">
                  Live data · Updated {fmtTime(timestamp)}
                </span>
              )}
              <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {summary?.driving || 0} driving
              </span>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 bg-white px-6">
          <nav className="-mb-px flex gap-1">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <span className="text-sm">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content — map tab is full-bleed, others have padding */}
        {error && (
          <div className="px-6 pt-3">
            <ErrorBanner message={error} onDismiss={() => setError('')} />
          </div>
        )}

        {activeTab === 'live-map' && (
          <LiveMapTab vehicles={vehicles} summary={summary} timestamp={timestamp}
            loading={loading} onRefresh={fetchVehicles} countdown={countdown} />
        )}

        {activeTab !== 'live-map' && (
          <div className="mx-auto max-w-screen-2xl px-6 py-5">
            {activeTab === 'fleet-intelligence' && (
              <FleetIntelligenceTab vehicles={vehicles} summary={summary} timestamp={timestamp}
                loading={loading} onRefresh={fetchVehicles} countdown={countdown} />
            )}

            {activeTab === 'journey-history' && (
              <JourneyHistoryTab vehicles={vehicles} />
            )}

            {activeTab === 'job-tracker' && <JobTrackerTab />}

            {activeTab === 'fleet-match' && <FleetMatchTab />}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
